import { useRef, useMemo } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import {
  Environment,
  GradientTexture,
  Float,
  MeshTransmissionMaterial,
} from "@react-three/drei";
import * as THREE from "three";
import WaveParticles from "./WaveParticles";
import FloatingBubbles from "./FloatingBubbles";
import Caustics from "./Caustics";

export default function OceanScene() {
  const oceanRef = useRef();
  const causticsRef = useRef();
  const { viewport } = useThree();

  // Create ocean surface with animated vertices (optimized)
  const geometry = useMemo(() => {
    const geo = new THREE.PlaneGeometry(50, 50, 32, 32);
    return geo;
  }, []);

  // Animate ocean waves
  useFrame(({ clock }) => {
    if (!oceanRef.current) return;

    const time = clock.getElapsedTime();
    const positions = oceanRef.current.geometry.attributes.position;

    for (let i = 0; i < positions.count; i++) {
      const x = positions.getX(i);
      const y = positions.getY(i);

      const wave1 = Math.sin(x * 0.3 + time * 0.5) * 0.15;
      const wave2 = Math.cos(y * 0.4 + time * 0.3) * 0.1;
      const wave3 = Math.sin((x + y) * 0.2 + time * 0.7) * 0.08;

      positions.setZ(i, wave1 + wave2 + wave3);
    }

    positions.needsUpdate = true;
    oceanRef.current.geometry.computeVertexNormals();

    // Rotate caustics slowly
    if (causticsRef.current) {
      causticsRef.current.rotation.z = time * 0.05;
    }
  });

  return (
    <>
      {/* Ambient Ocean Lighting */}
      <ambientLight intensity={0.2} color="#4a9ec9" />
      <directionalLight
        position={[10, 10, 5]}
        intensity={0.5}
        color="#7ec8e3"
        castShadow
      />
      <pointLight position={[0, 5, 0]} intensity={0.3} color="#f4c430" />

      {/* Volumetric Fog Effect */}
      <fog attach="fog" args={["#0a1628", 5, 25]} />

      {/* Ocean Surface */}
      <mesh
        ref={oceanRef}
        rotation={[-Math.PI / 2, 0, 0]}
        position={[0, -3, -5]}
        geometry={geometry}
      >
        <MeshTransmissionMaterial
          transmission={0.95}
          thickness={2}
          roughness={0.1}
          chromaticAberration={0.5}
          anisotropy={1}
          distortion={0.3}
          distortionScale={0.5}
          temporalDistortion={0.2}
          color="#1a4d6d"
          transparent
          opacity={0.8}
        />
      </mesh>

      {/* Ocean Floor with Gradient */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -5, -5]}>
        <planeGeometry args={[100, 100]} />
        <meshStandardMaterial>
          <GradientTexture
            stops={[0, 0.5, 1]}
            colors={["#0a1628", "#0d2847", "#1a4d6d"]}
            size={1024}
          />
        </meshStandardMaterial>
      </mesh>

      {/* Caustics Light Effect */}
      <Caustics ref={causticsRef} />

      {/* Floating Particles */}
      <WaveParticles count={200} />

      {/* Bubbles Rising */}
      <FloatingBubbles count={10} />

      {/* Floating Jellyfish-like Spheres - Optimized */}
      <Float
        speed={1.5}
        rotationIntensity={0.3}
        floatIntensity={0.8}
        floatingRange={[-1, 1]}
      >
        <mesh position={[-3, 0, -2]}>
          <sphereGeometry args={[0.3, 16, 16]} />
          <meshStandardMaterial
            color="#4a9ec9"
            emissive="#7ec8e3"
            emissiveIntensity={0.2}
            transparent
            opacity={0.7}
          />
        </mesh>
      </Float>

      <Float
        speed={1.2}
        rotationIntensity={0.2}
        floatIntensity={0.6}
        floatingRange={[-1, 1]}
      >
        <mesh position={[4, -1, -3]}>
          <sphereGeometry args={[0.4, 16, 16]} />
          <meshStandardMaterial
            color="#2d6a8f"
            emissive="#4a9ec9"
            emissiveIntensity={0.15}
            transparent
            opacity={0.7}
          />
        </mesh>
      </Float>

      {/* Environment Map */}
      <Environment preset="dawn" />
    </>
  );
}
